/**
 * @file WaiterStatsModal.tsx
 * @description Modal dialog presenting personal waiter sales, tables served,
 * real date/time order audits, and prior years annual totals.
 */

import React from 'react';
import { X, TrendingUp, ShoppingBag, Users, DollarSign, Award, Lock, Clock, Calendar, CheckCircle2 } from 'lucide-react';
import { useWaiterStats } from '../hooks/useWaiterStats';
import type { WaiterStatsTimeframe } from '../../../types/waiter.types';

interface WaiterStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: string | null;
  waiterName: string;
}

const TIMEFRAME_OPTIONS: { id: WaiterStatsTimeframe; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: '6months', label: '6 Months' },
  { id: 'year', label: 'Year' },
];

export const WaiterStatsModal: React.FC<WaiterStatsModalProps> = ({
  isOpen,
  onClose,
  venueId,
  waiterName,
}) => {
  const { stats, timeframe, setTimeframe, isLoading } = useWaiterStats(venueId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-xl bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90dvh] pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-0">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center pt-2.5 pb-1 bg-zinc-900/60 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        {/* Header */}
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 min-w-0 pr-2">
            <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-black text-white uppercase truncate">{waiterName}'s Performance</h2>
              <p className="text-xs text-zinc-400 truncate">Shift turnaround & personal sales tracking</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors shrink-0 cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Timeframe Switcher */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Timeframe</span>
            <div className="flex items-center gap-1 bg-zinc-900 p-1 rounded-xl border border-zinc-800 self-start sm:self-auto overflow-x-auto max-w-full">
              {TIMEFRAME_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTimeframe(opt.id)}
                  className={`px-3 py-1.5 min-h-[32px] rounded-lg text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
                    timeframe === opt.id
                      ? 'bg-indigo-600 text-white font-black shadow-sm'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12 text-zinc-500 text-sm animate-pulse">
              Calculating personal statistics...
            </div>
          ) : !stats ? (
            <div className="text-center py-12 text-zinc-500 text-sm">
              No statistics available.
            </div>
          ) : (
            <>
              {/* Primary Counts Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <div className="p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 text-center">
                  <div className="flex items-center justify-center text-indigo-400 mb-1">
                    <ShoppingBag className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-mono font-black text-white">{stats.orders_count}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Orders</p>
                </div>

                <div className="p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 text-center">
                  <div className="flex items-center justify-center text-blue-400 mb-1">
                    <Users className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-mono font-black text-white">{stats.tables_served}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Tables</p>
                </div>

                <div className="p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 text-center">
                  <div className="flex items-center justify-center text-amber-400 mb-1">
                    <Award className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-mono font-black text-white">{stats.items_sold}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Items Sold</p>
                </div>

                <div className="p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800 text-center">
                  <div className="flex items-center justify-center text-emerald-400 mb-1">
                    <Clock className="w-4 h-4" />
                  </div>
                  <p className="text-2xl font-mono font-black text-white">
                    {stats.avg_service_minutes ?? 0}m
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Avg Turnaround</p>
                </div>
              </div>

              {/* Financial Performance (Owner Permission Gated) */}
              <div className="space-y-2 pt-1">
                {stats.permissions.show_sales_amount ? (
                  <div className="p-4 rounded-2xl bg-emerald-950/20 border border-emerald-800/40 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-400 font-bold uppercase tracking-wider">
                        Personal Sales Handled ({timeframe.toUpperCase()})
                      </p>
                      <p className="text-2xl font-mono font-black text-emerald-300 mt-1">
                        {stats.sales_handled !== null ? stats.sales_handled.toLocaleString() : '0'} MAD
                      </p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400">
                      <DollarSign className="w-6 h-6" />
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/60 flex items-center gap-2 text-zinc-500 text-xs">
                    <Lock className="w-4 h-4 shrink-0" />
                    <span>Sales amounts are restricted by venue management policy.</span>
                  </div>
                )}

                {stats.permissions.show_avg_ticket && stats.average_ticket !== null && (
                  <div className="p-3.5 rounded-2xl bg-zinc-900/60 border border-zinc-800 flex items-center justify-between">
                    <span className="text-xs text-zinc-400 font-medium">Average Ticket Size</span>
                    <span className="font-mono font-bold text-sm text-zinc-200">
                      {stats.average_ticket.toFixed(2)} MAD
                    </span>
                  </div>
                )}
              </div>

              {/* Real Date & Time Recent Delivered Orders */}
              {stats.recent_orders && stats.recent_orders.length > 0 && (
                <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-zinc-300 text-xs font-bold uppercase tracking-wider">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span>Recent Delivered Orders (Live Audit Log)</span>
                    </div>
                    <span className="text-[11px] text-zinc-500 font-mono">Last 8 tickets</span>
                  </div>
                  <div className="space-y-1.5">
                    {stats.recent_orders.map(order => {
                      const servedDate = order.served_at ? new Date(order.served_at) : new Date(order.created_at);
                      const formattedTime = servedDate.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: stats.timezone || undefined,
                      });
                      const formattedDate = servedDate.toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                        timeZone: stats.timezone || undefined,
                      });

                      return (
                        <div
                          key={order.order_id}
                          className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-950/60 border border-zinc-800/80 text-xs"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="font-mono font-bold text-white shrink-0">{order.order_number}</span>
                            {order.table_id && (
                              <span className="text-zinc-400 truncate">Table {order.table_id}</span>
                            )}
                            <span className="text-zinc-500 hidden sm:inline">{order.items_count} items</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="font-mono text-zinc-400 text-[11px]">
                              {formattedDate} {formattedTime}
                            </span>
                            <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-zinc-800 text-emerald-400">
                              {order.total_amount.toLocaleString()} MAD
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Prior Years Annual Totals Section */}
              {stats.prior_years_totals && stats.prior_years_totals.length > 0 && (
                <div className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-800/80">
                  <div className="flex items-center gap-2 text-zinc-300 text-xs font-bold uppercase tracking-wider mb-2">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <span>Prior Years Performance (Annual Totals)</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mb-3">
                    Historical annual rollups for previous years.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-zinc-800 text-zinc-500">
                          <th className="pb-2 font-bold">Year</th>
                          <th className="pb-2 font-bold">Orders</th>
                          <th className="pb-2 font-bold">Tables</th>
                          <th className="pb-2 font-bold">Items</th>
                          {stats.permissions.show_sales_amount && (
                            <th className="pb-2 font-bold text-right">Total Sales</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/50">
                        {stats.prior_years_totals.map(pt => (
                          <tr key={pt.year} className="text-zinc-300">
                            <td className="py-2.5 font-mono font-bold text-white">{pt.year}</td>
                            <td className="py-2.5 font-mono">{pt.orders_count.toLocaleString()}</td>
                            <td className="py-2.5 font-mono">{pt.tables_served.toLocaleString()}</td>
                            <td className="py-2.5 font-mono">{pt.items_sold.toLocaleString()}</td>
                            {stats.permissions.show_sales_amount && (
                              <td className="py-2.5 font-mono text-right text-emerald-300">
                                {pt.total_sales.toLocaleString()} MAD
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-900/40 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 min-h-[44px] rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-sm transition-colors active:scale-[0.98] cursor-pointer"
          >
            Close My Performance
          </button>
        </div>
      </div>
    </div>
  );
};


